import { useCallback, useEffect, useState } from 'react';
import { useSubpantalla } from '../ui/navegacionReact';
import { ShieldAlert, Plus, X, ClipboardX, CalendarClock } from 'lucide-react';
import { api, ErrorApi } from '../api';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import ChipEstado from '../ui/ChipEstado';
import { ESTADO_NC, SEVERIDAD_NC } from '../ui/estados';
import { ORIGENES_NC, SEVERIDADES_NC } from '../ui/calidad';
import FichaNoConformidad from './FichaNoConformidad';
import { useAuth } from '../lib/contextoAuth';
import { Cargando } from '../ui/EstadoDePantalla';

// NO CONFORMIDADES · Fase 4 del módulo de Calidad.
//
// QUÉ CONTESTA ESTA PANTALLA. La pregunta que hace un cliente cuando algo
// salió mal: «¿qué hicieron para que no vuelva a pasar?». Y una segunda
// que decide si esto se usa de verdad: «¿qué me toca hacer a mí?».
//
// TRES DECISIONES DE USUARIO CERO QUE GOBIERNAN ESTO:
//
//   1. **Lo mío va primero.** Un 8D lleno de acciones con responsable y
//      sin ningún sitio donde esa persona las vea es un 8D que se queda
//      escrito. Arriba del todo, y en rojo si ya se pasó la fecha.
//   2. **Reportar cuesta dos campos.** Quien ve el defecto está en la
//      línea y con prisa. Un formulario de doce campos es un defecto que
//      no se reporta: se apaña la pieza y sigue.
//   3. **La lista dice lo que reclama acción.** Una no conformidad
//      abierta con tres acciones vencidas y una recién abierta se ven
//      igual en una lista de folios y fechas, y son cosas muy distintas.

function FormularioAlta({ onCreada, onCancelar }) {
  const [origen, setOrigen] = useState('PROCESO');
  const [descripcion, setDescripcion] = useState('');
  const [severidad, setSeveridad] = useState('MEDIA');
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const falta = descripcion.trim().length < 10
    ? 'contar qué pasó con un poco más de detalle' : null;

  async function enviar(e) {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      onCreada(await api.post('/no-conformidades', {
        origen, descripcion, severidad,
      }));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form onSubmit={enviar} className="border border-gray-200 rounded-xl p-4 space-y-3 bg-white">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Reportar algo que salió mal</h3>
        <button type="button" onClick={onCancelar}
                className="text-gray-500 hover:text-gray-700" aria-label="Cancelar">
          <X size={18} />
        </button>
      </div>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">Dónde se detectó</span>
        <select value={origen} onChange={(e) => setOrigen(e.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
          {ORIGENES_NC.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
        </select>
      </label>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">Qué pasó</span>
        <textarea
          value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={3}
          placeholder="La prensa dejó marca en el borde de unas cincuenta piezas del turno de la tarde."
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <span className="block text-xs text-gray-500 mt-1">
          Con el detalle que tengas ahora. Quien lo lea dentro de tres
          semanas tiene que entenderlo sin preguntarte.
        </span>
      </label>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">Gravedad</span>
        <select value={severidad} onChange={(e) => setSeveridad(e.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
          {SEVERIDADES_NC.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
        </select>
      </label>

      <p className="text-xs text-gray-500">
        El producto, los lotes y el análisis se ponen después desde la ficha.
        Lo importante es que quede reportado ahora.
      </p>

      {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      <div className="flex items-center gap-3">
        <button type="submit" disabled={!!falta || guardando}
                className="bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50">
          {guardando ? 'Guardando…' : 'Reportarlo'}
        </button>
        {falta && <span className="text-sm text-gray-500">Falta {falta}.</span>}
      </div>
    </form>
  );
}

export default function NoConformidades() {
  const { puede } = useAuth();
  const [lista, setLista] = useState(null);
  const [mias, setMias] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [error, setError] = useState(null);
  const [alta, setAlta] = useState(false);
  const [abierta, setAbierta] = useState(null);
  useSubpantalla('nc', !!abierta, () => setAbierta(null));
  const [verCerradas, setVerCerradas] = useState(false);

  const cargar = useCallback(() => {
    Promise.all([
      api.get(`/no-conformidades${verCerradas ? '' : '?solo_abiertas=true'}`),
      api.get('/no-conformidades/mis-acciones'),
    ])
      .then(([l, m]) => { setLista(l.no_conformidades); setMias(m); })
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar.'));
  }, [verCerradas]);

  useEffect(cargar, [cargar]);

  // Las personas, para poder asignar acciones. Falla en silencio a
  // propósito: quien no puede listar usuarios sigue pudiendo trabajar la
  // no conformidad, solo que sin poner responsable.
  useEffect(() => {
    let vivo = true;
    api.get('/usuarios')
      .then((u) => vivo && setPersonas(Array.isArray(u) ? u : (u.usuarios || [])))
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  async function abrir(id) {
    try {
      setAbierta(await api.get(`/no-conformidades/${id}`));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo abrir.');
    }
  }

  if (abierta) {
    return (
      <FichaNoConformidad
        inicial={abierta} personas={personas}
        onVolver={() => { setAbierta(null); cargar(); }}
        onCambio={cargar}
      />
    );
  }

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-bold text-gray-900">No conformidades</h1>
        <p className="text-sm text-gray-600">
          Lo que salió mal y qué se hizo para evitar que vuelva a pasar. Es lo
          que pide un cliente cuando le llega una pieza mala.
        </p>
      </header>

      {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      {/* LO MÍO, PRIMERO. */}
      {mias && mias.acciones.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <CalendarClock size={18} className="text-amber-600" />
            Lo que me toca a mí ({mias.acciones.length}
            {mias.vencidas > 0 ? `, ${mias.vencidas} ya pasada(s)` : ''})
          </h2>
          {mias.acciones.map((a) => (
            <button
              key={a.id} onClick={() => abrir(a.id_no_conformidad)}
              className={`w-full text-left border rounded-xl p-3 ${
                a.vencida ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}
            >
              <div className="text-sm font-semibold text-gray-900">{a.descripcion}</div>
              <div className="text-sm text-gray-700">
                No conformidad {a.folio}
                {a.fecha_compromiso
                  ? ` · para el ${a.fecha_compromiso}${a.vencida ? ' · YA SE PASÓ' : ''}`
                  : ' · sin fecha'}
              </div>
            </button>
          ))}
        </section>
      )}

      {alta ? (
        <FormularioAlta
          onCancelar={() => setAlta(false)}
          onCreada={(d) => { setAlta(false); setAbierta(d); cargar(); }}
        />
      ) : puede('calidad.no_conformidad_abrir') && (
        <button
          onClick={() => setAlta(true)}
          className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold"
        >
          <Plus size={16} /> Reportar algo que salió mal
        </button>
      )}

      {lista === null && <Cargando filas={4} />}

      {lista !== null && lista.length === 0 && !alta && (
        <EstadoVacio
          icono={ClipboardX}
          titulo={verCerradas ? 'No hay ninguna no conformidad'
            : 'No hay ninguna no conformidad abierta'}
          descripcion={
            'Una no conformidad es algo que salió mal y lo que se hizo al '
            + 'respecto: qué pasó, qué se hizo con el material, por qué pasó '
            + 'y qué se cambió para que no vuelva. TEGO abre una sola cuando '
            + 'una inspección sale rechazada o cuando se libera un lote con '
            + 'concesión.'
          }
          accion={puede('calidad.no_conformidad_abrir') && (
            <button
              onClick={() => setAlta(true)}
              className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold"
            >
              <Plus size={16} /> Reportar la primera
            </button>
          )}
        />
      )}

      {lista !== null && lista.length > 0 && (
        <section className="space-y-2">
          <NotaExplicativa titulo="Cerrar una no conformidad no se puede deshacer">
            Su 8D se le entrega a alguien: a partir de ahí no se
            modifica ni se reabre. Y no se cierra mientras queden acciones
            correctivas pendientes.
          </NotaExplicativa>

          {lista.map((n) => (
            <button
              key={n.id} onClick={() => abrir(n.id)}
              className="w-full text-left border border-gray-200 rounded-xl p-3 bg-white hover:border-gray-300"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900">
                    {n.folio} · {n.origen_nombre}
                    {n.sku ? ` · ${n.sku}` : ''}
                  </div>
                  <div className="text-sm text-gray-700 line-clamp-2">
                    {n.descripcion}
                  </div>
                  <div className="text-sm text-gray-600">
                    {n.fecha}
                    {n.acciones_pendientes > 0
                      && ` · ${n.acciones_pendientes} acción(es) pendiente(s)`}
                    {n.acciones_vencidas > 0 && (
                      <span className="text-red-700 font-semibold">
                        {' '}· {n.acciones_vencidas} ya pasada(s)
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <ChipEstado mapa={ESTADO_NC} clave={n.estado} />
                  <ChipEstado mapa={SEVERIDAD_NC} clave={n.severidad} />
                </div>
              </div>
            </button>
          ))}
        </section>
      )}

      <button
        onClick={() => setVerCerradas((v) => !v)}
        className="inline-flex items-center gap-2 text-sm text-gray-600 hover:underline"
      >
        <ShieldAlert size={16} />
        {verCerradas ? 'Ver solo las abiertas' : 'Ver también las cerradas'}
      </button>
    </div>
  );
}
